import { useMemo, useState } from 'react'
import * as stylex from '@stylexjs/stylex'

import Avatar from '../../components/Avatar'
import Button from '../../components/Button'
import EmptyState from '../../components/EmptyState'
import Icon from '../../components/Icon'
import SegmentedControl from '../../components/SegmentedControl'
import {
  expenseBalanceDelta,
  findPerson,
  formatMoney,
  formatTransactionDate,
  type Balance,
  type Overview,
} from '../../lib/ledger'
import { mobileStyles as styles } from '../mobileStyles'

const localStyles = stylex.create({
  balanceGrid: { marginTop: '14px', display: 'grid', gap: '8px' },
  balance: { borderRadius: '0.75rem', backgroundColor: '#f6faf5', padding: '12px' },
  balanceLabel: { fontSize: '11px', fontWeight: 600, color: '#647267' },
  balanceValue: { marginTop: '4px', fontSize: '19px', fontWeight: 700, letterSpacing: '-.03em', color: '#315b3c' },
  filters: { marginTop: '12px' },
  amountColumn: { flexShrink: 0, textAlign: 'right' },
  running: { marginTop: '3px', display: 'block', maxWidth: '145px', fontSize: '10px', color: '#879087' },
  settlementIcon: { backgroundColor: '#f5f0e7', color: '#997149' },
  notes: { marginTop: '12px', borderRadius: '0.75rem', backgroundColor: '#f6f8f3', padding: '10px 12px', fontSize: '11px', lineHeight: 1.5, color: '#59685e' },
  groups: { marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '6px' },
  groupChip: { borderRadius: '9999px', borderWidth: 1, borderColor: '#dbe6d9', backgroundColor: '#fff', paddingInline: '8px', paddingBlock: '4px', fontSize: '10px', fontWeight: 600, color: '#55705c' },
})

type PersonDetailMobilePageProps = {
  data: Overview
  personID: string
  onBack: () => void
  onEditPerson: () => void
  onOpenExpense: (expenseID: string) => void
  onAddExpense: () => void
  onRecordExchange: (fromCurrency?: string) => void
  onSettle: (balance: Balance) => void
  onShare: () => void
}

type Filter = 'all' | 'expense' | 'settlement'

type Event = {
  id: string
  kind: 'expense' | 'settlement'
  title: string
  detail: string
  date: string
  timeSet?: boolean
  currency: string
  deltaCents: number
  runningCents: number
  expenseID?: string
}

function balanceText(amountCents: number, currency: string, personName: string): string {
  if (amountCents > 0) return `${personName} owes you ${formatMoney(amountCents, currency)}`
  if (amountCents < 0) return `You owe ${personName} ${formatMoney(Math.abs(amountCents), currency)}`
  return `Settled up in ${currency}`
}

export default function PersonDetailMobilePage({
  data,
  personID,
  onBack,
  onEditPerson,
  onOpenExpense,
  onAddExpense,
  onRecordExchange,
  onSettle,
  onShare,
}: PersonDetailMobilePageProps) {
  const [filter, setFilter] = useState<Filter>('all')
  const person = findPerson(data, personID)

  const model = useMemo(() => {
    if (!person) return null

    const expenses = data.expenses.filter((expense) => {
      const payerAmounts = expense.payerAmounts ?? { [expense.paidById]: expense.amountCents }
      const personInvolved = payerAmounts[personID] !== undefined || expense.participantIds.includes(personID)
      const currentUserInvolved = payerAmounts[data.currentUser.id] !== undefined || expense.participantIds.includes(data.currentUser.id)
      return personInvolved && currentUserInvolved
    })
    const settlements = data.settlements.filter((settlement) => settlement.personId === personID)
    const balances = data.balances.filter((balance) => balance.personId === personID)
    const currencies = Array.from(new Set([
      ...expenses.map((expense) => expense.currency),
      ...settlements.map((settlement) => settlement.currency),
      ...balances.map((balance) => balance.currency),
    ])).sort()
    const currentByCurrency = new Map(balances.map((balance) => [balance.currency, balance.amountCents]))
    const raw = [
      ...expenses.map((expense) => ({
        id: `expense:${expense.id}`,
        kind: 'expense' as const,
        title: expense.description,
        detail: (expense.payerAmounts?.[data.currentUser.id] ?? (expense.paidById === data.currentUser.id ? expense.amountCents : 0)) > 0 ? 'You paid' : `${person.name} paid`,
        date: expense.occurredAt ?? expense.createdAt,
        timeSet: expense.occurredTimeSet,
        currency: expense.currency,
        deltaCents: expenseBalanceDelta(expense, data.currentUser.id, personID),
        expenseID: expense.id,
      })),
      ...settlements.map((settlement) => ({
        id: `settlement:${settlement.id}`,
        kind: 'settlement' as const,
        title: settlement.method === 'currency_exchange' ? 'Currency exchange' : settlement.direction === 'received' ? `Payment from ${person.name}` : `Payment to ${person.name}`,
        detail: [settlement.direction === 'received' ? `${person.name} paid you` : `You paid ${person.name}`, settlement.description].filter(Boolean).join(' · '),
        date: settlement.paidAt ?? settlement.createdAt,
        timeSet: settlement.paidTimeSet,
        currency: settlement.currency,
        deltaCents: settlement.direction === 'received' ? -settlement.amountCents : settlement.amountCents,
      })),
    ]
    const totals = new Map<string, number>()
    raw.forEach((event) => totals.set(event.currency, (totals.get(event.currency) ?? 0) + event.deltaCents))
    const running = new Map<string, number>()
    currencies.forEach((currency) => running.set(currency, (currentByCurrency.get(currency) ?? 0) - (totals.get(currency) ?? 0)))
    const events = [...raw]
      .sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime())
      .map((event) => {
        const runningCents = (running.get(event.currency) ?? 0) + event.deltaCents
        running.set(event.currency, runningCents)
        return { ...event, runningCents }
      })
      .reverse() as Event[]

    return { balances, currencies, currentByCurrency, events }
  }, [data, person, personID])

  if (!person || !model) return null

  const personNotes = 'notes' in person ? person.notes : undefined
  const visibleEvents = model.events.filter((event) => filter === 'all' || event.kind === filter).slice(0, 30)
  const displayBalances = model.currencies.map((currency) => ({ currency, amountCents: model.currentByCurrency.get(currency) ?? 0 }))
  const settleable = displayBalances.filter((balance) => balance.amountCents !== 0)
  const groupsTogether = data.groups.filter((group) => group.memberIds.includes(personID))

  return <div {...stylex.props(styles.page)}>
    <Button onClick={onBack} variant="secondary"><Icon name="arrow-left" />People</Button>

    <section {...stylex.props(styles.card)}>
      <div {...stylex.props(styles.avatarRow)}>
        <Avatar person={person} size={56} />
        <div {...stylex.props(styles.listMain)}><h1 {...stylex.props(styles.title)}>{person.name}</h1><p {...stylex.props(styles.subtitle)}>{person.email || 'Shared ledger'}</p></div>
        <Button aria-label="Edit person" onClick={onEditPerson} size="sm" variant="secondary">Edit</Button>
      </div>
      <div {...stylex.props(localStyles.balanceGrid)}>
        {displayBalances.length === 0 ? <div {...stylex.props(localStyles.balance)}><p {...stylex.props(localStyles.balanceLabel)}>Current balance</p><p {...stylex.props(localStyles.balanceValue)}>Settled up</p></div> : displayBalances.map((balance) => <div {...stylex.props(localStyles.balance)} key={balance.currency}><p {...stylex.props(localStyles.balanceLabel)}>{balance.amountCents > 0 ? `${person.name} owes you` : balance.amountCents < 0 ? `You owe ${person.name}` : 'Settled up'}</p><p {...stylex.props(localStyles.balanceValue)}>{formatMoney(Math.abs(balance.amountCents), balance.currency)}</p></div>)}
      </div>
      {personNotes && <div {...stylex.props(localStyles.notes)}><strong>Private note:</strong> {personNotes}</div>}
      {groupsTogether.length > 0 && <div {...stylex.props(localStyles.groups)}>{groupsTogether.map((group) => <span {...stylex.props(localStyles.groupChip)} key={group.id}>{group.name}</span>)}</div>}
      <div {...stylex.props(styles.rowActions)}>
        {settleable.map((balance) => <Button key={balance.currency} onClick={() => onSettle({ personId: personID, amountCents: balance.amountCents, currency: balance.currency })} size="sm"><Icon name="settle" />{settleable.length === 1 ? 'Settle' : `Settle ${balance.currency}`}</Button>)}
        <Button onClick={onAddExpense} size="sm" variant={settleable.length > 0 ? 'secondary' : 'primary'}><Icon name="plus" />Add expense</Button>
        <Button onClick={() => onRecordExchange()} size="sm" variant="secondary"><Icon name="settle" />Exchange</Button>
        <Button onClick={onShare} size="sm" variant="secondary">Share</Button>
      </div>
    </section>

    <section {...stylex.props(styles.card)}>
      <h2 {...stylex.props(styles.cardTitle)}>Activity</h2>
      <p {...stylex.props(styles.muted)}>Every expense and payment with a running balance.</p>
      <div {...stylex.props(localStyles.filters)}><SegmentedControl<Filter> onChange={setFilter} options={[{ label: 'All', value: 'all' }, { label: 'Expenses', value: 'expense' }, { label: 'Payments', value: 'settlement' }]} value={filter} /></div>
      {visibleEvents.length === 0 ? <EmptyState description="No matching activity yet." /> : <div {...stylex.props(styles.list)}>{visibleEvents.map((event) => {
        const sign = event.deltaCents > 0 ? '+' : event.deltaCents < 0 ? '−' : ''
        return <button {...stylex.props(styles.listItem)} key={event.id} onClick={() => event.expenseID && onOpenExpense(event.expenseID)} type="button"><span {...stylex.props(styles.emptyIcon, event.kind === 'settlement' && localStyles.settlementIcon)}><Icon name={event.kind === 'expense' ? 'receipt' : 'settle'} /></span><span {...stylex.props(styles.listMain)}><span {...stylex.props(styles.listTitle)}>{event.title}</span><span {...stylex.props(styles.listMeta)}>{event.detail} · {formatTransactionDate(event.date, Boolean(event.timeSet))}</span></span><span {...stylex.props(localStyles.amountColumn)}><span {...stylex.props(styles.listAmount, event.deltaCents >= 0 ? styles.positive : styles.negative)}>{sign}{formatMoney(Math.abs(event.deltaCents), event.currency)}</span><span {...stylex.props(localStyles.running)}>{balanceText(event.runningCents, event.currency, person.name)}</span></span></button>
      })}</div>}
    </section>
  </div>
}
